import React, { useState, useEffect, useMemo } from 'react';
import { auth, db } from '../firebase';
import { collection, doc, onSnapshot, query, where, orderBy, getDoc } from 'firebase/firestore';
import { productDisplayNo } from '../utils/productDisplayId';
import { bacaSaizGambar, simpanSaizGambar } from '../utils/saizGambarSenarai';
import { repairPendingLibraryCards, pendingReasonText } from '../services/productReadyService';
import ProductLibraryModalV39 from './ProductLibraryModalV39';

const FOLDER_ID = 'product';

/* Paparan senarai v3.9 (list/grid) diingat per-browser.
   LALAI = 'jadual' (senarai) — sama seperti halaman Products, supaya kedua-dua
   tempat pelanggan memilih produk bermula dengan rupa yang SAMA.
   ⛔ Nilai ditulis HANYA bila pengguna menekan suis paparan (lihat tukarPapar
      di bawah), BUKAN pada setiap mount. Versi lama menulisnya dalam useEffect
      yang turut berjalan pada mount, jadi setiap browser yang pernah membuka
      modal ini terus menyimpan nilai lalai semasa — dan menukar lalai kelak
      langsung tidak berkesan untuk mereka. */
const KUNCI_PAPAR = 'sesaat_v39_library_papar';

const bacaPapar = () => {
    try {
        const v = localStorage.getItem(KUNCI_PAPAR);
        return v === 'grid' ? 'grid' : 'jadual';
    } catch { return 'jadual'; }
};

/* `uiV39` datang sebagai prop dari App.jsx — SEMUA logik (Firestore, kunci
   PENDING, had trial, kuantiti, tulisan ke hilir) kekal di sini; hanya
   rupanya bercabang. Lihat nota di kepala ProductLibraryModalV39.jsx. */
export default function ProductLibraryModal({ isOpen, onClose, onSelectProducts, uiV39 = false }) {
    const [systemItems, setSystemItems] = useState([]);
    const [ownItems, setOwnItems] = useState([]);
    const [systemLoading, setSystemLoading] = useState(true);
    const [ownLoading, setOwnLoading] = useState(true);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [searchQuery, setSearchQuery] = useState('');
    const [tab, setTab] = useState('system'); // 'system' | 'own'
    const [sortBy, setSortBy] = useState('date'); // 'number' | 'name' | 'date' — default Newest (System + My Products)
    const [groupFilter, setGroupFilter] = useState('all'); // 'all' | groupId
    const [productGroups, setProductGroups] = useState([]);
    const [userLibraryGroups, setUserLibraryGroups] = useState([]);
    const [userTier, setUserTier] = useState(null);
    const [quantities, setQuantities] = useState({}); // { [productId]: number }
    const [recentSystemIds, setRecentSystemIds] = useState([]);
    const [recentOwnIds, setRecentOwnIds] = useState([]);

    /* ---- Keadaan khusus rupa v3.9 -----------------------------------
       Susunan v3.9 membawa ARAH (naik/turun) dan jenis "Status" yang
       tiada dalam `sortBy` lama. Ia disimpan berasingan supaya rupa lama
       — laluan patah balik bila suis mati — langsung tidak berubah. */
    const [susun, setSusun] = useState('masa');
    const [arah, setArah] = useState('desc');
    const [papar, setPapar] = useState(bacaPapar);
    const tukarPapar = (mod) => {
        setPapar(mod);
        try { localStorage.setItem(KUNCI_PAPAR, mod); } catch { /* mod privasi */ }
    };

    /* Saiz gambar mod senarai — DIKONGSI dengan halaman Products (satu kunci
       sahaja; lihat utils/saizGambarSenarai.js). ⛔ Ditulis hanya pada klik
       pengguna, jangan pindahkan ke useEffect. */
    const [saizGambar, setSaizGambar] = useState(bacaSaizGambar);
    const tukarSaizGambar = (v) => { setSaizGambar(v); simpanSaizGambar(v); };

    const userEmail = auth.currentUser?.email;
    const isTrial = userTier === 'trial';

    // Read user tier once when modal opens
    useEffect(() => {
        if (!isOpen) return;
        const uid = auth.currentUser?.uid;
        if (!uid) return;
        getDoc(doc(db, 'users', uid)).then(snap => {
            if (snap.exists()) setUserTier(snap.data().tier ?? null);
        });
    }, [isOpen]);

    // Force back to system tab if trial user somehow lands on own tab
    useEffect(() => {
        if (isTrial && tab === 'own') setTab('system');
    }, [isTrial, tab]);

    // Load System items — semua auth user boleh baca
    useEffect(() => {
        if (!isOpen) return;
        setSystemLoading(true);
        const q = query(
            collection(db, 'library', FOLDER_ID, 'items'),
            where('group', '==', 'system'),
            orderBy('number', 'asc')
        );
        const unsub = onSnapshot(q, (snap) => {
            setSystemItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            setSystemLoading(false);
        }, (err) => {
            console.error('[ProductLibraryModal] system read error:', err);
            setSystemLoading(false);
        });
        return () => unsub();
    }, [isOpen]);

    // Load Own items — user sendiri sahaja (match firestore rule baru)
    useEffect(() => {
        if (!isOpen) return;
        const email = auth.currentUser?.email;
        if (!email) { setOwnLoading(false); return; }
        setOwnLoading(true);
        const q = query(
            collection(db, 'library', FOLDER_ID, 'items'),
            where('group', '==', 'own'),
            where('addedBy', '==', email),
            orderBy('number', 'asc')
        );
        const unsub = onSnapshot(q, (snap) => {
            const loaded = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setOwnItems(loaded);
            setOwnLoading(false);

            // ═══ AUTO-PULIH KAD PENDING (senyap) ═══
            // Dijalankan di sini juga — bukan pada page Products sahaja — kerana
            // di modal inilah pelanggan terlanggar kad terkunci. Tulisan pulih
            // mencetuskan snapshot baharu, jadi senarai kemas kini sendiri;
            // pengadang sesi dalam servis menghalang pusingan berulang.
            repairPendingLibraryCards({ folderId: FOLDER_ID, products: loaded, email })
                .catch(err => console.warn('[ProductLibraryModal] auto-pulih gagal:', err?.message));
        }, (err) => {
            console.error('[ProductLibraryModal] own read error:', err);
            setOwnLoading(false);
        });
        return () => unsub();
    }, [isOpen]);

    // Merged view + loading state derived from both listeners
    const items = useMemo(() => [...systemItems, ...ownItems], [systemItems, ownItems]);
    const loading = systemLoading || ownLoading;

    // Load productGroups from folder doc (System tab — admin managed)
    useEffect(() => {
        if (!isOpen) return;
        const unsub = onSnapshot(doc(db, 'library', FOLDER_ID), (snap) => {
            setProductGroups(snap.data()?.productGroups || []);
        });
        return () => unsub();
    }, [isOpen]);

    // Load user-specific groups (My Products tab — per user)
    useEffect(() => {
        if (!isOpen) return;
        const uid = auth.currentUser?.uid;
        if (!uid) return;
        const unsub = onSnapshot(doc(db, 'users', uid), (snap) => {
            setUserLibraryGroups(snap.exists() ? (snap.data().libraryGroups || []) : []);
        });
        return () => unsub();
    }, [isOpen]);

    // Active groups — ikut tab semasa
    const activeGroups = tab === 'own' ? userLibraryGroups : productGroups;

    // Load recently used IDs from localStorage when modal opens
    useEffect(() => {
        if (!isOpen) return;
        try {
            const sys = JSON.parse(localStorage.getItem('sesaat:recentLibrary:system') || '[]');
            const own = JSON.parse(localStorage.getItem('sesaat:recentLibrary:own') || '[]');
            setRecentSystemIds(Array.isArray(sys) ? sys : []);
            setRecentOwnIds(Array.isArray(own) ? own : []);
        } catch { /* ignore parse errors */ }
    }, [isOpen]);

    // Reset when closed
    useEffect(() => {
        if (!isOpen) {
            setSelectedIds(new Set());
            setSearchQuery('');
            setGroupFilter('all');
            setQuantities({});
        }
    }, [isOpen]);

    // Counts per tab (pre-search)
    const systemCount = useMemo(
        () => items.filter(p => !p.group || p.group === 'system').length,
        [items]
    );
    const ownCount = useMemo(
        () => items.filter(p => p.group === 'own' && p.addedBy === userEmail).length,
        [items, userEmail]
    );

    // Filtered + sorted by tab
    const filtered = useMemo(() => {
        let result = items.filter(p => {
            if (tab === 'system') return !p.group || p.group === 'system';
            return p.group === 'own' && p.addedBy === userEmail;
        });

        // Group filter
        if (groupFilter !== 'all') {
            result = result.filter(p => p.customGroup === groupFilter);
        }

        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            result = result.filter(p =>
                p.name?.toLowerCase().includes(q) ||
                String(p.number || '').includes(q) ||
                p.link?.toLowerCase().includes(q)
            );
        }

        /* Rupa v3.9 memakai susunan BERARAH dan menyusun ikut ID produk
           TikTok yang benar-benar dipapar pada baris (pilihan user
           2026-08-14) — bukan `number` turutan sheet, yang tidak
           kelihatan di mana-mana dalam modal ini.

           ⚠️ ID dibandingkan sebagai TEKS dengan pilihan `numeric`, bukan
              ditukar kepada Number: ID TikTok 19 digit melebihi julat
              integer selamat JavaScript, dan dua ID berlainan boleh
              menjadi nombor yang SAMA selepas ditukar. */
        if (uiV39) {
            const banding = {
                nombor: (a, b) => String(productDisplayNo(a)).localeCompare(String(productDisplayNo(b)), undefined, { numeric: true }),
                masa: (a, b) => {
                    const da = a.addedAt?.toDate?.() || new Date(a.addedAt || 0);
                    const db = b.addedAt?.toDate?.() || new Date(b.addedAt || 0);
                    return da - db;
                },
                nama: (a, b) => (a.name || '').localeCompare(b.name || ''),
                status: (a, b) => (!!a.hasIntelligence === !!b.hasIntelligence ? 0 : a.hasIntelligence ? 1 : -1),
            }[susun] || (() => 0);

            const darab = arah === 'asc' ? 1 : -1;
            return [...result].sort((a, b) => banding(a, b) * darab);
        }

        if (sortBy === 'name') {
            result = [...result].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        } else if (sortBy === 'date') {
            result = [...result].sort((a, b) => {
                const da = a.addedAt?.toDate?.() || new Date(a.addedAt || 0);
                const db = b.addedAt?.toDate?.() || new Date(b.addedAt || 0);
                return db - da;
            });
        } else {
            result = [...result].sort((a, b) => (a.number || 0) - (b.number || 0));
        }

        return result;
    }, [items, tab, searchQuery, sortBy, userEmail, groupFilter, uiV39, susun, arah]);

    // Produk PENDING (tiada Intelligence) dikunci pada KEDUA-DUA tab — Stage 1+2 dah OFF,
    // jadi produk tanpa data memang tak boleh generate di workspace. Kad My Products yang
    // PENDING dilengkapkan semula melalui Import Products di Extension.
    const selectableIds = useMemo(
        () => filtered.filter(p => p.hasIntelligence).map(p => p.id),
        [filtered]
    );

    // Bilangan produk PENDING dalam paparan semasa — untuk nota panduan tab My Products
    const pendingCount = filtered.length - selectableIds.length;

    // Keyboard shortcut: Ctrl+A — select/deselect all selectable (ready) products
    useEffect(() => {
        if (!isOpen) return;
        const onKeyDown = (e) => {
            if (!(e.ctrlKey || e.metaKey) || e.key !== 'a') return;
            const tag = document.activeElement?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA') return;
            e.preventDefault();
            setSelectedIds(prev => {
                if (prev.size === selectableIds.length) {
                    setQuantities({});
                    return new Set();
                }
                setQuantities(Object.fromEntries(selectableIds.map(id => [id, 1])));
                return new Set(selectableIds);
            });
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isOpen, selectableIds]);

    if (!isOpen) return null;

    const toggleSelect = (id) => {
        const product = items.find(p => p.id === id);
        // Produk PENDING dikunci pada kedua-dua tab (System + My Products)
        if (!product?.hasIntelligence) return;
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
                setQuantities(q => { const n = { ...q }; delete n[id]; return n; });
            } else {
                next.add(id);
                setQuantities(q => ({ ...q, [id]: 1 }));
            }
            return next;
        });
    };

    const changeQty = (id, delta) => {
        setQuantities(q => {
            const cur = q[id] || 1;
            const next = Math.max(1, Math.min(20, cur + delta));
            return { ...q, [id]: next };
        });
    };

    const selectAll = () => {
        if (selectedIds.size === selectableIds.length) {
            setSelectedIds(new Set());
            setQuantities({});
        } else {
            setSelectedIds(new Set(selectableIds));
            setQuantities(Object.fromEntries(selectableIds.map(id => [id, 1])));
        }
    };

    const clearSelection = () => {
        setSelectedIds(new Set());
        setQuantities({});
    };

    const handleSubmit = () => {
        const selected = items.filter(p => selectedIds.has(p.id));
        if (selected.length === 0) return;
        // Expand by quantity — 1 product × qty N = N copies, hilir akan jadikan N product card
        const expanded = [];
        selected.forEach(p => {
            const qty = quantities[p.id] || 1;
            for (let i = 0; i < qty; i++) expanded.push(p);
        });
        // Track recently used per tab (unique, max 5, newest first)
        const systemIds = selected.filter(p => !p.group || p.group === 'system').map(p => p.id);
        const ownIds = selected.filter(p => p.group === 'own').map(p => p.id);
        if (systemIds.length > 0) {
            const updated = Array.from(new Set([...systemIds, ...recentSystemIds])).slice(0, 5);
            setRecentSystemIds(updated);
            try { localStorage.setItem('sesaat:recentLibrary:system', JSON.stringify(updated)); } catch { /* ignore */ }
        }
        if (ownIds.length > 0) {
            const updated = Array.from(new Set([...ownIds, ...recentOwnIds])).slice(0, 5);
            setRecentOwnIds(updated);
            try { localStorage.setItem('sesaat:recentLibrary:own', JSON.stringify(updated)); } catch { /* ignore */ }
        }
        onSelectProducts(expanded);
        setSelectedIds(new Set());
        setQuantities({});
        setSearchQuery('');
        onClose();
    };

    const handleClose = () => {
        setSelectedIds(new Set());
        setQuantities({});
        setSearchQuery('');
        onClose();
    };

    const switchTab = (next) => {
        if (tab === next) return;
        setTab(next);
        setSelectedIds(new Set());
        setQuantities({});
        setSearchQuery('');
        setGroupFilter('all');
    };

    // Derived values
    const totalQty = Array.from(selectedIds).reduce((sum, id) => sum + (quantities[id] || 1), 0);
    const selectedProducts = items.filter(p => selectedIds.has(p.id));
    const showCart = selectedIds.size > 0;

    // Recently used items — filtered ke tab semasa
    const recentItems = (tab === 'own' ? recentOwnIds : recentSystemIds)
        .map(id => items.find(p => p.id === id))
        .filter(Boolean)
        .filter(p => tab === 'system' ? (!p.group || p.group === 'system') : (p.group === 'own' && p.addedBy === userEmail))
        .slice(0, 5);

    /* ═══ Rupa v3.9 ═══════════════════════════════════════════════════
       Bercabang di SINI, selepas semua state dan pengendali disiapkan,
       supaya kedua-dua rupa berkongsi otak yang sama betul.

       "Guna baru-baru ini" sengaja TIDAK dihantar (arahan user
       2026-08-14) — jalur itu dibuang daripada rupa baharu. Logiknya
       kekal hidup: `handleSubmit` masih merekod produk yang diguna,
       jadi rupa lama masih memaparkannya dan tiada data hilang kalau
       suis dimatikan semula. */
    if (uiV39) {
        return (
            <ProductLibraryModalV39
                senarai={filtered}
                terpilihIds={selectedIds}
                kuantiti={quantities}
                bolehPilihIds={selectableIds}
                bilPending={pendingCount}
                produkTerpilih={selectedProducts}
                jumlahKuantiti={totalQty}
                kiraSystem={systemCount}
                kiraOwn={ownCount}
                isTrial={isTrial}
                tab={tab}
                cari={searchQuery}
                kumpulanAktif={groupFilter}
                groupPilihan={activeGroups}
                namaGroup={(p) => activeGroups.find((g) => g.id === p.customGroup)?.name || null}
                susun={susun}
                arah={arah}
                papar={papar}
                saizGambar={saizGambar}
                onTukarSaiz={tukarSaizGambar}
                memuat={tab === 'own' ? ownLoading : systemLoading}
                idPapar={productDisplayNo}
                onTukarTab={switchTab}
                onCari={setSearchQuery}
                onTukarGroup={setGroupFilter}
                onTukarSusun={(j, a) => { setSusun(j); setArah(a); }}
                onTukarPapar={tukarPapar}
                onPetik={toggleSelect}
                onUbahKuantiti={changeQty}
                onPilihSemua={selectAll}
                onKosongkan={clearSelection}
                onHantar={handleSubmit}
                onTutup={handleClose}
            />
        );
    }

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-[fadeIn_0.2s_ease-out]" onClick={handleClose} />

            {/* Telefon: senarai produk + bakul disusun MENEGAK. Sebelum ini
                bakul selebar 256px duduk di sebelah senarai — pada skrin 375px
                itu meninggalkan lebih kurang 75px untuk senarai produk. */}
            <div className="relative flex flex-col md:flex-row items-stretch gap-3 mx-4 max-h-[85vh] w-full md:w-auto">

            <div className="relative w-full max-w-lg max-h-[85vh] min-h-0 bg-white dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-2xl flex flex-col animate-[slideUp_0.3s_ease-out]">

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-orange-500/20">
                            <span className="material-symbols-outlined text-white text-[18px]">local_library</span>
                        </div>
                        <div>
                            <h2 className="text-sm font-bold text-slate-800 dark:text-white">Product Library</h2>
                            <p className="text-[10px] text-slate-400 font-medium">Pilih produk dari library</p>
                        </div>
                    </div>
                    <button onClick={handleClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 transition-colors">
                        <span className="material-symbols-outlined text-[20px]">close</span>
                    </button>
                </div>

                {/* Tabs + Search + Sort (single row) */}
                <div className="px-6 py-3 border-b border-slate-100 dark:border-slate-800/50 flex items-center gap-3">
                    {/* Tabs */}
                    <div className="flex items-center gap-1 shrink-0">
                        {[
                            { key: 'system', label: 'System', icon: 'auto_awesome', count: systemCount },
                            { key: 'own', label: 'My Products', icon: 'person', count: ownCount },
                        ].map(t => {
                            const isLocked = t.key === 'own' && isTrial;
                            return (
                                <button
                                    key={t.key}
                                    onClick={() => !isLocked && switchTab(t.key)}
                                    title={isLocked ? 'My Products hanya untuk pengguna berbayar' : undefined}
                                    className={`flex items-center gap-1.5 px-2.5 h-8 rounded-lg text-[11px] font-bold transition-all ${
                                        isLocked
                                            ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed'
                                            : tab === t.key
                                                ? 'text-slate-800 dark:text-slate-100 bg-slate-100 dark:bg-slate-800'
                                                : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                                    }`}
                                >
                                    <span className="material-symbols-outlined text-[14px]" style={{ fontVariationSettings: tab === t.key ? "'FILL' 1" : undefined }}>{t.icon}</span>
                                    {t.label}
                                    {isLocked
                                        ? <span className="material-symbols-outlined text-[12px] text-slate-300 dark:text-slate-600">lock</span>
                                        : <span className={`text-[10px] font-extrabold ${tab === t.key ? 'text-slate-500' : 'text-slate-300'}`}>{t.count}</span>
                                    }
                                </button>
                            );
                        })}
                    </div>

                    {/* Divider */}
                    <div className="h-6 w-px bg-slate-200 dark:bg-slate-700 shrink-0" />

                    {/* Search */}
                    <div className="relative flex-1 min-w-0">
                        <span className="material-symbols-outlined absolute left-2.5 top-1/2 -translate-y-1/2 text-[15px] text-slate-400">search</span>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Cari..."
                            className="w-full h-8 pl-8 pr-2 text-[11px] font-medium text-slate-700 dark:text-slate-200 bg-transparent border-0 focus:outline-none placeholder:text-slate-300"
                        />
                    </div>

                    {/* Sort */}
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="h-8 px-2 text-[11px] font-bold text-slate-500 dark:text-slate-400 bg-transparent border-0 focus:outline-none cursor-pointer shrink-0"
                        title="Sort"
                    >
                        <option value="number">Number</option>
                        <option value="name">Name</option>
                        <option value="date">Newest</option>
                    </select>
                </div>

                {/* Recently Used Products */}
                {recentItems.length > 0 && !searchQuery && (
                    <div className="px-4 pt-2.5 pb-2 border-b border-slate-100 dark:border-slate-800/50">
                        <div className="flex items-center gap-1 mb-1.5">
                            <span className="material-symbols-outlined text-[12px] text-slate-400">schedule</span>
                            <span className="text-[9px] font-extrabold text-slate-400 uppercase tracking-wider">Guna Baru-baru Ini</span>
                        </div>
                        <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5 -mx-0.5 px-0.5 custom-scrollbar">
                            {recentItems.map(p => {
                                const isSelected = selectedIds.has(p.id);
                                const isLocked = !p.hasIntelligence;
                                return (
                                    <button
                                        key={p.id}
                                        onClick={() => !isLocked && toggleSelect(p.id)}
                                        disabled={isLocked}
                                        title={`${p.name}${isLocked ? ' (Pending)' : ''}`}
                                        className={`shrink-0 flex items-center gap-1.5 pl-1 pr-2 py-1 rounded-lg border transition-all ${
                                            isSelected
                                                ? 'bg-primary/10 border-primary/40 text-primary'
                                                : isLocked
                                                    ? 'border-slate-200 dark:border-slate-700 opacity-40 cursor-not-allowed'
                                                    : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:border-slate-400 text-slate-600 dark:text-slate-300'
                                        }`}
                                    >
                                        <div className="w-7 h-7 rounded-md overflow-hidden bg-slate-100 dark:bg-slate-800 shrink-0">
                                            {p.imageUrl ? (
                                                <img src={p.imageUrl} alt="" className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <span className="material-symbols-outlined text-[12px] text-slate-300">image</span>
                                                </div>
                                            )}
                                        </div>
                                        <span className="text-[10px] font-extrabold tabular-nums max-w-[92px] truncate" title={`ID produk: ${productDisplayNo(p)}`}>#{productDisplayNo(p)}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Group Filter Pills */}
                {activeGroups.length > 0 && (
                    <div className="px-4 pt-2.5 pb-2 border-b border-slate-100 dark:border-slate-800/50 flex items-center gap-1.5 flex-wrap">
                        <button
                            onClick={() => setGroupFilter('all')}
                            className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border transition-all ${
                                groupFilter === 'all'
                                    ? 'bg-slate-800 dark:bg-white text-white dark:text-slate-900 border-slate-800 dark:border-white'
                                    : 'bg-white dark:bg-slate-900 text-slate-500 border-slate-200 dark:border-slate-700 hover:border-slate-400'
                            }`}
                        >
                            <span className="material-symbols-outlined text-[11px]">label</span>
                            Semua Group
                        </button>
                        {activeGroups.map(g => {
                            const isActive = groupFilter === g.id;
                            const count = items.filter(p => {
                                const inTab = tab === 'system' ? (!p.group || p.group === 'system') : (p.group === 'own' && p.addedBy === userEmail);
                                return inTab && p.customGroup === g.id;
                            }).length;
                            return (
                                <button
                                    key={g.id}
                                    onClick={() => setGroupFilter(isActive ? 'all' : g.id)}
                                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold border transition-all ${
                                        isActive ? 'text-white border-transparent shadow-sm' : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-slate-400'
                                    }`}
                                    style={isActive ? { backgroundColor: g.color, borderColor: g.color } : {}}
                                >
                                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: isActive ? 'rgba(255,255,255,0.7)' : g.color }}></span>
                                    {g.name}
                                    <span className={`text-[9px] font-extrabold ml-0.5 ${isActive ? 'text-white/80' : 'text-slate-400'}`}>{count}</span>
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Product List */}
                <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-2">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin"></div>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <span className="material-symbols-outlined text-3xl text-slate-300 mb-2">inventory_2</span>
                            <p className="text-xs text-slate-400 font-medium">
                                {searchQuery
                                    ? 'Tiada hasil carian'
                                    : tab === 'own'
                                        ? 'Belum ada produk. Pergi ke Library › Products › My Products untuk tambah.'
                                        : 'Admin belum menambah produk System.'}
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Select All — only counts READY products (pending excluded) */}
                            <div className="flex items-center gap-2 pb-2 border-b border-slate-100 dark:border-slate-800 mb-2">
                                <label className={`flex items-center gap-2 ${selectableIds.length > 0 ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                                    <input
                                        type="checkbox"
                                        checked={selectableIds.length > 0 && selectedIds.size === selectableIds.length}
                                        onChange={selectAll}
                                        disabled={selectableIds.length === 0}
                                        className="w-3.5 h-3.5 rounded border-slate-300 text-primary focus:ring-primary"
                                    />
                                    <span className="text-[10px] font-bold text-slate-400 uppercase">Pilih Semua ({selectableIds.length})</span>
                                </label>
                            </div>

                            {/* Nota panduan — kad My Products yang PENDING tidak boleh dipilih */}
                            {tab === 'own' && pendingCount > 0 && (
                                <div className="flex items-start gap-1.5 px-2.5 py-2 mb-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200/60 dark:border-amber-800/40">
                                    <span className="material-symbols-outlined text-[13px] text-amber-500 shrink-0 mt-px">info</span>
                                    <p className="text-[10px] text-amber-700 dark:text-amber-400 font-medium leading-relaxed m-0">
                                        <span className="font-extrabold">{pendingCount} produk</span> berstatus <span className="font-extrabold">PENDING</span> tidak boleh dipilih kerana data produk belum lengkap. Sila lengkapkan produk tersebut melalui <span className="font-extrabold">Import Products</span> di Extension, kemudian cuba semula.
                                    </p>
                                </div>
                            )}

                            {filtered.map(product => {
                                const isPending = !product.hasIntelligence;
                                const isSelected = selectedIds.has(product.id);
                                return (
                                    <div
                                        key={product.id}
                                        onClick={() => toggleSelect(product.id)}
                                        title={isPending
                                            ? (tab === 'own'
                                                ? `Data produk belum lengkap. ${pendingReasonText(product) || ''} Lengkapkan melalui Import Products di Extension.`.replace(/\s+/g, ' ').trim()
                                                : 'Produk belum Ready — Intelligence data belum di-generate')
                                            : undefined}
                                        className={`flex items-center gap-3 p-3 rounded-xl border transition-all duration-200 ${
                                            isPending
                                                ? 'bg-slate-50 dark:bg-slate-900/40 border-slate-200 dark:border-slate-800 opacity-60 cursor-not-allowed'
                                                : isSelected
                                                    ? 'bg-primary/5 border-primary/30 shadow-sm cursor-pointer'
                                                    : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 cursor-pointer'
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => toggleSelect(product.id)}
                                            onClick={(e) => e.stopPropagation()}
                                            disabled={isPending}
                                            className="w-3.5 h-3.5 rounded border-slate-300 text-primary focus:ring-primary shrink-0 disabled:cursor-not-allowed"
                                        />
                                        <div className="w-10 aspect-square rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800 shrink-0">
                                            {product.imageUrl ? (
                                                <img alt="" className="w-full h-full object-cover" src={product.imageUrl} />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <span className="material-symbols-outlined text-[16px] text-slate-300">image</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className="text-[11px] font-bold text-indigo-500 whitespace-nowrap tabular-nums" title="ID produk TikTok">#{productDisplayNo(product)}</span>
                                                {/* Badge status — gaya sama macam halaman Products (LibraryView) */}
                                                {product.hasIntelligence ? (
                                                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30 border border-emerald-200/60 dark:border-emerald-700/40" title="Intelligence Data Ready">
                                                        <span className="material-symbols-outlined text-[10px] text-emerald-500" style={{ fontVariationSettings: "'FILL' 1" }}>auto_awesome</span>
                                                        <span className="text-[8px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Ready</span>
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 border border-slate-200/60 dark:border-slate-700/40" title="Belum ada Intelligence Data">
                                                        <span className="material-symbols-outlined text-[10px] text-slate-400">pending</span>
                                                        <span className="text-[8px] font-bold text-slate-400 uppercase tracking-wider">Pending</span>
                                                    </span>
                                                )}
                                                <span className="text-[11px] font-bold text-slate-700 dark:text-slate-200 uppercase truncate">{product.name}</span>
                                            </div>
                                            {product.link && (
                                                <p className="text-[9px] text-slate-400 truncate">{product.link}</p>
                                            )}
                                            {/* Sebab kad ini terkunci — tanpa ini pelanggan cuma nampak kad
                                                kelabu yang tak boleh ditekan, tanpa tahu apa yang kurang. */}
                                            {isPending && pendingReasonText(product) && (
                                                <p className="flex items-start gap-1 mt-0.5 text-[9px] leading-snug text-amber-600 dark:text-amber-400 m-0">
                                                    <span className="material-symbols-outlined text-[11px] shrink-0">info</span>
                                                    <span className="min-w-0">{pendingReasonText(product)}</span>
                                                </p>
                                            )}
                                        </div>
                                        {isSelected && !isPending && (
                                            <div
                                                className="flex items-center gap-1 shrink-0"
                                                onClick={(e) => e.stopPropagation()}
                                                title="Bilangan product card untuk dijana"
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() => changeQty(product.id, -1)}
                                                    disabled={(quantities[product.id] || 1) <= 1}
                                                    className="w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                                >
                                                    <span className="material-symbols-outlined text-[14px]">remove</span>
                                                </button>
                                                <span className="text-[11px] font-extrabold text-primary w-5 text-center tabular-nums">{quantities[product.id] || 1}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => changeQty(product.id, 1)}
                                                    disabled={(quantities[product.id] || 1) >= 20}
                                                    className="w-6 h-6 rounded-full bg-primary/10 hover:bg-primary/20 text-primary flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                                >
                                                    <span className="material-symbols-outlined text-[14px]">add</span>
                                                </button>
                                            </div>
                                        )}
                                        {isPending && (
                                            <span className="material-symbols-outlined text-slate-400 text-[16px] shrink-0">lock</span>
                                        )}
                                    </div>
                                );
                            })}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 space-y-3">
                    <div className="flex items-center justify-between">
                        <span className="text-[10px] text-slate-400 font-medium">
                            {selectedIds.size} produk dipilih
                            {totalQty !== selectedIds.size && <span className="text-primary font-bold"> · {totalQty} card</span>}
                        </span>
                        <div className="flex items-center gap-2">
                            <button onClick={handleClose} className="px-4 py-2 rounded-lg text-[11px] font-bold text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                                Batal
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={selectedIds.size === 0}
                                className="flex items-center gap-1.5 px-5 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white text-[11px] font-bold shadow-lg shadow-orange-500/20 active:scale-[0.97] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <span className="material-symbols-outlined text-[15px]">add_shopping_cart</span>
                                Tambah ({totalQty})
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Cart Sidebar — muncul bila ada selection */}
            {showCart && (
                <aside className="w-full md:w-64 shrink-0 max-h-[32vh] md:max-h-[85vh] bg-white dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-2xl flex flex-col animate-[slideLeft_0.25s_ease-out] overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800">
                        <div className="flex items-center gap-2">
                            <span className="material-symbols-outlined text-[16px] text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>shopping_cart</span>
                            <span className="text-[11px] font-bold text-slate-800 dark:text-white">Terpilih ({selectedIds.size})</span>
                        </div>
                        <button
                            onClick={clearSelection}
                            className="text-[9px] font-bold text-slate-400 hover:text-red-500 uppercase tracking-wide transition-colors"
                            title="Kosongkan semua"
                        >
                            Kosongkan
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto custom-scrollbar p-2 space-y-1.5">
                        {selectedProducts.map(p => (
                            <div
                                key={p.id}
                                className="group flex items-center gap-2 p-2 rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 transition-colors"
                            >
                                <div className="w-8 h-8 rounded-md overflow-hidden bg-slate-100 dark:bg-slate-800 shrink-0">
                                    {p.imageUrl ? (
                                        <img src={p.imageUrl} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <span className="material-symbols-outlined text-[12px] text-slate-300">image</span>
                                        </div>
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[9px] font-extrabold text-indigo-500 tabular-nums truncate" title="ID produk TikTok">#{productDisplayNo(p)}</div>
                                    <div className="text-[10px] font-bold text-slate-700 dark:text-slate-200 truncate uppercase" title={p.name}>{p.name}</div>
                                </div>
                                <div className="flex items-center gap-0.5 shrink-0">
                                    <button
                                        type="button"
                                        onClick={() => changeQty(p.id, -1)}
                                        disabled={(quantities[p.id] || 1) <= 1}
                                        className="w-5 h-5 rounded-full bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                    >
                                        <span className="material-symbols-outlined text-[11px]">remove</span>
                                    </button>
                                    <span className="text-[10px] font-extrabold text-primary w-4 text-center tabular-nums">{quantities[p.id] || 1}</span>
                                    <button
                                        type="button"
                                        onClick={() => changeQty(p.id, 1)}
                                        disabled={(quantities[p.id] || 1) >= 20}
                                        className="w-5 h-5 rounded-full bg-primary/10 hover:bg-primary/20 text-primary flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                    >
                                        <span className="material-symbols-outlined text-[11px]">add</span>
                                    </button>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => toggleSelect(p.id)}
                                    title="Buang dari senarai"
                                    className="w-5 h-5 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-red-100 dark:hover:bg-red-900/30 text-slate-400 hover:text-red-500 flex items-center justify-center shrink-0 opacity-0 group-hover:opacity-100 transition-all"
                                >
                                    <span className="material-symbols-outlined text-[12px]">close</span>
                                </button>
                            </div>
                        ))}
                    </div>
                    <div className="px-4 py-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
                        <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Total Card</div>
                        <div className="text-lg font-extrabold text-primary tabular-nums">{totalQty}</div>
                    </div>
                </aside>
            )}

            </div>

            <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes slideLeft {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
        </div>
    );
}
